import React, { useState, useEffect, useMemo, useContext } from 'react'

import Button from '../../components/elements/Button'
import Icon from '../../components/elements/Icon'
import IconText from '../../components/elements/IconText'

import Search from '../../components/inputs/Search'
import Textarea from '../../components/inputs/Textarea'

import SearchResultRow from '../../components/structures/SearchResultRow'

import { SocialContext } from '../../context/socialContext'
import { useSearchEventUsers, useInviteTeam } from '../../context/atomContext'

import { debounce, printClasses } from '../../Helpers'

type Props = {
  teamId: number,
  dismissPanel: () => mixed,
}

function TeamInvites(props: Props) {
  const { teamId, dismissPanel } = props

  // State
  const [inviteEmail, setInviteEmail] = useState('')
  const [peopleToInvite, setPeopleToInvite] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [searchTermDebounced, setSearchTermDebounced] = useState('')
  const [results, setResults] = useState([])
  const [hasResults, setHasResults] = useState(false)
  const [expandResults, setExpandResults] = useState(false)
  const [expandInvites, setExpandInvites] = useState(true)

  // Context
  const { openProfile } = useContext(SocialContext)

  // API
  const { data: searchData, isFetching } = useSearchEventUsers(
    searchTermDebounced,
    searchTermDebounced.length > 1,
  )
  const inviteToTeam = useInviteTeam()

  // test data
  // Variables
  const debug = false
  useEffect(() => setResults(searchData?.value?.rows), [searchData])
  useEffect(() => setHasResults(results?.length > 0), [results])

  // functions
  const inviteMembers = () => {
    if (debug) console.log(peopleToInvite)

    const invites = peopleToInvite.map(person => {
      if (person.email) return { email: person.email }
      if (person.id) return { userId: person.id }

      return {}
    })

    const postData = {
      teamId,
      invites,
    }

    inviteToTeam.mutate(postData)
    dismissPanel()
  }

  const debounceSearch = useMemo(
    () => debounce(term => setSearchTermDebounced(term), 500),
    [setSearchTermDebounced],
  )

  const doSearch = value => {
    setSearchTerm(value)
    debounceSearch(value)
    toggleShowResults(!value)
  }

  const inviteEmailUsers = list => {
    const emailUsers = []

    list.forEach(email => {
      if (email) {
        const exists = peopleToInvite.filter(person =>
          Object.is(person.email, email),
        )

        if (exists.length === 0) {
          emailUsers.push({
            id: email,
            email,
            name: null,
            type: 'email',
          })
        }
      }
    })

    setPeopleToInvite([...peopleToInvite, ...emailUsers])
  }

  const handleEmailInvites = () => {
    const list = inviteEmail.split(/[\n, ]+/).filter(Boolean)
    inviteEmailUsers(list)
    setInviteEmail('')
  }

  const addInviteUser = user => {
    const exists = peopleToInvite.filter(person =>
      Object.is(person.id, user.id),
    )

    if (debug) console.log(exists)

    if (exists.length === 0) {
      setPeopleToInvite([...peopleToInvite, user])
    }
  }

  const removeInviteUser = user => {
    if (user?.email) {
      const newPeople = peopleToInvite.filter(
        person => !Object.is(person.email, user.email),
      )
      setPeopleToInvite(newPeople)
    } else {
      const newPeople = peopleToInvite.filter(
        person => !Object.is(person.id, user.id),
      )
      setPeopleToInvite(newPeople)
    }
  }

  const findUser = user =>
    peopleToInvite.find(item => Object.is(item.id, user.id))

  const toggleAddUser = user =>
    findUser(user) ? removeInviteUser(user) : addInviteUser(user)

  const toggleShowResults = value => {
    setExpandResults(!value)
  }

  const toggleExpandInvites = value => {
    setExpandInvites(!value)
  }

  if (debug) console.log(results)

  return (
    <>
      <section className={printClasses(['section-block'])}>
        <div className="rpm-header">
          <h5 className="text--caps text--bold">
            Email Invites <span className="text--small">(one per line)</span>
          </h5>
          <div className="flex--auto-gap">
            <Textarea
              id="inviteEmail"
              type="email"
              placeholder="Email"
              value={inviteEmail}
              changed={event => setInviteEmail(event.target.value)}
            />

            <Button
              kind="link"
              onClick={handleEmailInvites}
              disabled={!inviteEmail}
              label="Add"
            />
          </div>
        </div>
      </section>

      <section className={printClasses(['section-block', 'sticky-top'])}>
        <div className="rpm-header xflex--auto-gap xuse-baseline">
          <h5 className="text--caps">
            In App Invites{' '}
            <span className="text--small">(Existing Atom Users Only)</span>
          </h5>

          <Search
            id="searchUsers"
            placeholder="Search..."
            noResults={
              <IconText
                icon={{ type: 'fal', name: 'user-slash', size: 'lg' }}
                text="No users found, try again..."
                cnames="fg--accent"
              />
            }
            helpMessage={
              <IconText
                icon={{ type: 'fal', name: 'question-square', size: 'lg' }}
                text={
                  results?.length > 0
                    ? `Found ${results?.length} users`
                    : 'Use at least two characters'
                }
                cnames="ish"
              />
            }
            value={searchTerm}
            isSearching={isFetching}
            debouncedValue={searchTermDebounced}
            hasResults={hasResults}
            changed={doSearch}
            maxlength={20}
            cnames="use-thin-inputs"
          />
        </div>
      </section>

      <section
        className={printClasses([
          'section-block',
          'invites--search--results',
          'search--results',
          expandResults && 'is-open',
        ])}
      >
        <ul>
          {results?.map(aUser => (
            <SearchResultRow
              key={aUser.id}
              user={aUser}
              avatarSize={3}
              openProfile={() => openProfile(aUser.id)}
              cnames={aUser.isOnMyTeam && 'rpm-marquee ish'}
              isPublic
              action={
                <Button
                  kind="link"
                  onClick={() => toggleAddUser(aUser)}
                  disabled={aUser.isOnMyTeam}
                >
                  <Icon
                    type={findUser(aUser) ? 'fas' : 'fal'}
                    name="check-circle"
                    size="lg"
                  />
                </Button>
              }
            />
          ))}
        </ul>
      </section>

      <section
        className={printClasses([
          'section-block',
          'invites--pending',
          expandInvites ? 'sticky-top' : 'sticky-bottom',
        ])}
      >
        <div className="rpm-header">
          <h5 className="text--caps">
            Pending Invites{' '}
            <span className="text--small">({peopleToInvite.length})</span>
          </h5>
        </div>
      </section>

      <section
        className={printClasses([
          'section-block',
          'invites--pending--list',
          'search--results',
          !expandInvites && 'rpm-marquee',
          expandInvites && 'is-open',
        ])}
      >
        <ul>
          {peopleToInvite.map(aUser => (
            <SearchResultRow
              key={aUser.id}
              user={aUser}
              avatarSize={3}
              expanded={expandInvites}
              openProfile={
                expandInvites
                  ? () => openProfile(aUser.id)
                  : () => toggleExpandInvites(expandInvites)
              }
              isPublic
              action={
                <Button kind="link" onClick={() => removeInviteUser(aUser)}>
                  <Icon
                    type="fal"
                    name="times-circle"
                    size="lg"
                    cnames="fg--accent ish"
                  />
                </Button>
              }
              altAction={
                !expandInvites ? () => toggleExpandInvites(expandInvites) : null
              }
            />
          ))}
        </ul>
      </section>

      <section
        className={printClasses([
          'rpm-description',
          expandInvites && 'sticky-bottom',
        ])}
      >
        <Button
          label="Send Invites"
          disabled={peopleToInvite.length === 0}
          onClick={inviteMembers}
        />
      </section>
    </>
  )
}

export default TeamInvites
